'use client';

import * as EppoSdk from '@eppo/js-client-sdk';
import { AttributeType, init } from '@eppo/js-client-sdk';

import { useContext, useEffect } from 'react';

import { useSegmentAnalyticsContext } from '@/SegmentAnalyticsProvider';
import { useGetLSUserIdFromUser } from '@/hooks/useGetLSUserIdFromUser';
import {
  useCurrentOrganization,
  useOrganizationId,
  useUser,
} from '@/hooks/useSwr';
import { isSelfHosted } from '@/utils/is-self-hosted';
import { keysToSnakeCase } from '@/utils/jsonUtils';

import { EppoContext } from '../EppoContext';

// note: for new keys, please use snake case
type FeatureFlag =
  | 'thread_improvements'
  | 'Studio-Graph-Zoom-Controls'
  | 'New-Homepage-Get-Started-Experiment'
  | 'examplesTableToggles';

export const useInitializeEppo = () => {
  const { analytics } = useSegmentAnalyticsContext();
  useEffect(() => {
    const initEppo = async () => {
      if (isSelfHosted || !analytics) return;
      await init({
        apiKey: import.meta.env.VITE_EPPO_CLIENT_TOKEN,
        assignmentLogger: {
          logAssignment: (assignment) =>
            // send the assignment to segment
            analytics.track('Eppo Randomized Assignment', {
              lsUserId: assignment.subject,
              ...assignment,
            }),
        },
      });
    };
    initEppo();
  }, [analytics]);

  const eppoClient = EppoSdk.getInstance();
  return eppoClient;
};

export function useEppoFeatureFlag(opts: {
  featureFlag: FeatureFlag;
  flagType?: 'boolean';
  defaultValue?: boolean;
  additionalAttributes?: Record<string, AttributeType>;
}): { value: boolean | undefined; isLoading: boolean };

export function useEppoFeatureFlag(opts: {
  featureFlag: FeatureFlag;
  flagType: 'string';
  defaultValue?: string;
  additionalAttributes?: Record<string, AttributeType>;
}): { value: string | undefined; isLoading: boolean };

export function useEppoFeatureFlag(opts: {
  featureFlag: FeatureFlag;
  flagType: 'integer';
  defaultValue?: number;
  additionalAttributes?: Record<string, AttributeType>;
}): { value: number | undefined; isLoading: boolean };

export function useEppoFeatureFlag({
  featureFlag,
  flagType = 'boolean',
  defaultValue,
  additionalAttributes,
}: {
  featureFlag: string;
  flagType?: 'string' | 'boolean' | 'integer';
  defaultValue?: string | boolean | number;
  additionalAttributes?: Record<string, AttributeType>;
}): {
  value: string | boolean | number | undefined;
  isLoading: boolean;
} {
  const eppoClient = useContext(EppoContext);
  const { data: user, isLoading: userLoading } = useUser();
  const { data: organization, isLoading: orgLoading } =
    useCurrentOrganization();
  const tenantId = useOrganizationId();
  const { lsUserId, isLoading: lsUserIdLoading } = useGetLSUserIdFromUser(
    user?.data.user?.id
  );
  if (!eppoClient || !user || !user.data.user || !lsUserId || !organization?.id)
    return {
      value: undefined,
      isLoading: userLoading || lsUserIdLoading || orgLoading,
    };

  const attributes: Record<string, AttributeType> = keysToSnakeCase({
    lsUserId,
    userId: user.data.user.id,
    email: user.data.user.email ?? '',
    organizationId: organization.id,
    tenantId: tenantId ?? '',
    created_at: user.data.user.created_at,
    ...additionalAttributes,
  });

  if (flagType === 'boolean') {
    const value = eppoClient.getBooleanAssignment(
      featureFlag,
      lsUserId,
      attributes,
      (defaultValue ?? false) as boolean
    );
    return {
      value,
      isLoading: false,
    };
  }
  if (flagType === 'integer') {
    const value = eppoClient.getIntegerAssignment(
      featureFlag,
      lsUserId,
      attributes,
      (defaultValue ?? 0) as number
    );
    return {
      value,
      isLoading: false,
    };
  }
  const value = eppoClient.getStringAssignment(
    featureFlag,
    lsUserId,
    attributes,
    (defaultValue ?? '') as string
  );
  return {
    value,
    isLoading: false,
  };
}
